import type React from "react";
import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import AppThemeProvider from "./shared/theme/AppThemeProvider";

// Eager — shells e guardas partilhados por (quase) todas as rotas, no caminho
// crítico, e sem dependências pesadas próprias.
import { Wrapper } from "./Wrapper";
import { App } from "./App";
import PrivateRoute from "./features/auth/pages/PrivateRoute";
import AppLayout from "./shared/components/AppLayout";
import CondominiumLayout from "./shared/components/CondominiumLayout";

// Eager — páginas de autenticação: entrada dos utilizadores não autenticados,
// leves (formulários, sem grelhas/gráficos), não vale a pena atrasá-las.
import Register from "./features/auth/pages/Register";
import Login from "./features/auth/pages/Login";
import ForgotPassword from "./features/auth/pages/ForgotPassword";
import ResetPassword from "./features/auth/pages/ResetPassword";
import ConfirmEmail from "./features/auth/pages/ConfirmEmail";

// Lazy — todas as páginas por trás do PrivateRoute. Cada uma vira um chunk
// próprio e sai do bundle inicial (que servia ~2,8 MB de uma vez). O <Suspense>
// vive no <Routes> (topo) e à volta do <Outlet> do CondominiumLayout, para
// navegar dentro de um condomínio não apagar a sidebar nem o contexto activo.
const Dashboard = lazy(() => import("./features/dashboard/Dashboard"));
const ModuleOverview = lazy(() => import("./features/dashboard/ModuleOverview"));

const Properties = lazy(() => import("./features/condominio/Properties"));
const PropertyManagement = lazy(() => import("./features/property-management/PropertyManagement"));
const Units = lazy(() => import("./features/condominio/Units"));
const Residents = lazy(() => import("./features/condominio/Residents"));
const Committee = lazy(() => import("./features/condominio/Committee"));
const OccupancyHistory = lazy(() => import("./features/condominio/OccupancyHistory"));
const Buildings = lazy(() => import("./features/condominio/Buildings"));
const Blocks = lazy(() => import("./features/condominio/Blocks"));
const Users = lazy(() => import("./features/users/Users"));
const Profile = lazy(() => import("./features/profile/Profile"));
const SupportTickets = lazy(() => import("./features/support/SupportTickets"));
const Modules = lazy(() => import("./features/modules/Modules"));

const Settings = lazy(() => import("./features/settings/Settings"));
const SettingsIndexRedirect = lazy(() =>
    import("./features/settings/pages").then((m) => ({ default: m.SettingsIndexRedirect })));
const GeneralSettingsPage = lazy(() =>
    import("./features/settings/pages").then((m) => ({ default: m.GeneralSettingsPage })));
const RentalsSettingsPage = lazy(() =>
    import("./features/settings/pages").then((m) => ({ default: m.RentalsSettingsPage })));
const ParkingSettingsPage = lazy(() =>
    import("./features/settings/pages").then((m) => ({ default: m.ParkingSettingsPage })));
const FinanceSettingsPage = lazy(() =>
    import("./features/settings/pages").then((m) => ({ default: m.FinanceSettingsPage })));
const PurchasesSettingsPage = lazy(() =>
    import("./features/settings/pages").then((m) => ({ default: m.PurchasesSettingsPage })));
const TypesSettingsPage = lazy(() =>
    import("./features/settings/pages").then((m) => ({ default: m.TypesSettingsPage })));
const RolesSettingsPage = lazy(() =>
    import("./features/settings/pages").then((m) => ({ default: m.RolesSettingsPage })));

const CompanyLayout = lazy(() => import("./features/company/CompanyLayout"));
const CompanyData = lazy(() => import("./features/company/CompanyData"));
const CompanyInvoices = lazy(() => import("./features/company/CompanyInvoices"));
const CompanyPayments = lazy(() => import("./features/company/CompanyPayments"));

const Accounts = lazy(() => import("./features/finance/Accounts"));
const Revenues = lazy(() => import("./features/finance/Revenues"));
const Charges = lazy(() => import("./features/finance/Charges"));
const Payments = lazy(() => import("./features/finance/Payments"));
const Fornecedores = lazy(() => import("./features/finance/Fornecedores"));
const Despesas = lazy(() => import("./features/finance/Despesas"));
const RecurringExpenses = lazy(() => import("./features/finance/RecurringExpenses"));
const Purchases = lazy(() => import("./features/finance/Purchases"));
const DespesaPayments = lazy(() => import("./features/finance/DespesaPayments"));
const Transferencias = lazy(() => import("./features/finance/Transferencias"));

const AgendamentoVisitas = lazy(() => import("./features/access-control/AgendamentoVisitas"));
const Visitas = lazy(() => import("./features/access-control/Visitas"));
const Visitantes = lazy(() => import("./features/access-control/Visitantes"));
const Veiculos = lazy(() => import("./features/access-control/Veiculos"));
const RegistosVeiculo = lazy(() => import("./features/access-control/RegistosVeiculo"));
const Encomendas = lazy(() => import("./features/access-control/Encomendas"));
const AccessPoints = lazy(() => import("./features/access-control/AccessPoints"));
const Shifts = lazy(() => import("./features/access-control/Shifts"));

const EspacosComuns = lazy(() => import("./features/common-areas/EspacosComuns"));
const MinhasReservas = lazy(() => import("./features/common-areas/MinhasReservas"));
const AprovacaoReservas = lazy(() => import("./features/common-areas/AprovacaoReservas"));
const CalendarioReservas = lazy(() => import("./features/common-areas/CalendarioReservas"));
const HistoricoReservas = lazy(() => import("./features/common-areas/HistoricoReservas"));

const Parking = lazy(() => import("./features/parking/Parking"));
const ParkingSlots = lazy(() => import("./features/parking/ParkingSlots"));
const ParkingAssignments = lazy(() => import("./features/parking/ParkingAssignments"));
const ParkingAuthorizations = lazy(() => import("./features/parking/ParkingAuthorizations"));

const Assets = lazy(() => import("./features/maintenance/Assets"));
const AssetMaintenanceHistory = lazy(() => import("./features/maintenance/AssetMaintenanceHistory"));
const MaintenanceRequests = lazy(() => import("./features/maintenance/MaintenanceRequests"));
const MaintenanceRequestForm = lazy(() => import("./features/maintenance/MaintenanceRequestForm"));
const MaintenanceRequestDetail = lazy(() => import("./features/maintenance/MaintenanceRequestDetail"));
const ServiceOrders = lazy(() => import("./features/maintenance/ServiceOrders"));
const ServiceOrderForm = lazy(() => import("./features/maintenance/ServiceOrderForm"));
const ServiceOrderDetail = lazy(() => import("./features/maintenance/ServiceOrderDetail"));
const MaintenanceSchedules = lazy(() => import("./features/maintenance/MaintenanceSchedules"));
const MaintenanceScheduleForm = lazy(() => import("./features/maintenance/MaintenanceScheduleForm"));
const SlaConfigSettings = lazy(() => import("./features/maintenance/SlaConfigSettings"));

const Comunicados = lazy(() => import("./features/comunicacao/Comunicados"));
const ComunicadoForm = lazy(() => import("./features/comunicacao/ComunicadoForm"));
const ComunicadoDetail = lazy(() => import("./features/comunicacao/ComunicadoDetail"));
const Templates = lazy(() => import("./features/comunicacao/Templates"));
const Documentos = lazy(() => import("./features/comunicacao/Documentos"));

const Assembleias = lazy(() => import("./features/assembleias/Assembleias"));
const AssembleiaForm = lazy(() => import("./features/assembleias/AssembleiaForm"));
const AssembleiaDetail = lazy(() => import("./features/assembleias/AssembleiaDetail"));
const Votacoes = lazy(() => import("./features/assembleias/Votacoes"));
const VotacaoForm = lazy(() => import("./features/assembleias/VotacaoForm"));
const VotacaoDetail = lazy(() => import("./features/assembleias/VotacaoDetail"));

const ArrendamentosIndex = lazy(() => import("./features/arrendamentos/ArrendamentosIndex"));
const MeusArrendamentos = lazy(() => import("./features/arrendamentos/MeusArrendamentos"));
const CobrancasRenda = lazy(() => import("./features/arrendamentos/CobrancasRenda"));
const PagamentosRenda = lazy(() => import("./features/arrendamentos/PagamentosRenda"));
const MeuArrendamento = lazy(() => import("./features/arrendamentos/MeuArrendamento"));
const MinhasFaturas = lazy(() => import("./features/arrendamentos/MinhasFaturas"));


// Fallback enquanto o chunk de uma rota lazy é descarregado.
const RouteFallback = () => (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress />
    </Box>
);

export const Rotas: React.FC = () => {

    return (
        <AppThemeProvider>
            <BrowserRouter>
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                    <Route path="/" element={<Wrapper />} >
                        <Route index element={<App />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/confirm-email" element={<ConfirmEmail />} />

                        {/* /dashboard era um placeholder ("em desenvolvimento") — o início é a raiz. */}
                        <Route path="/dashboard" element={<Navigate to="/" replace />} />

                        //Lista de propriedades (condomínios e estates) pertencentes ao tenant
                        <Route
                            path="/properties"
                            element={
                                <PrivateRoute>
                                        <Properties />
                                </PrivateRoute>
                            }
                        />

                        {/* Gestão de propriedades (imóveis avulsos) — destino de property.<domínio>, ver Properties.tsx/config.ts */}
                        <Route
                            path="/empreendimentos/:id"
                            element={
                                <PrivateRoute>
                                    <AppLayout withSidebar={false}>
                                        <PropertyManagement />
                                    </AppLayout>
                                </PrivateRoute>
                            }
                        />

                        <Route
                            path="/profile"
                            element={
                                <PrivateRoute>
                                    <AppLayout withSidebar={false}>
                                        <Profile />
                                    </AppLayout>
                                </PrivateRoute>
                            }
                        />

                        {/* Empresa — item próprio no menu do utilizador; sidebar: cadastro / faturas / pagamentos */}
                        <Route
                            path="/company"
                            element={
                                <PrivateRoute>
                                    <AppLayout withSidebar={false}>
                                        <CompanyLayout />
                                    </AppLayout>
                                </PrivateRoute>
                            }
                        >
                            <Route index element={<Navigate to="cadastro" replace />} />
                            <Route path="cadastro" element={<CompanyData embedded />} />
                            <Route path="faturas" element={<CompanyInvoices />} />
                            <Route path="pagamentos" element={<CompanyPayments />} />
                            <Route path="*" element={<Navigate to="cadastro" replace />} />
                        </Route>

                        {/* Suporte — acompanhamento dos pedidos submetidos (novos pedidos: widget flutuante) */}
                        <Route
                            path="/support"
                            element={
                                <PrivateRoute>
                                    <AppLayout withSidebar={false}>
                                        <SupportTickets />
                                    </AppLayout>
                                </PrivateRoute>
                            }
                        />

                        <Route
                            path="/modules"
                            element={
                                <PrivateRoute>
                                    <AppLayout withSidebar={false}>
                                        <Modules />
                                    </AppLayout>
                                </PrivateRoute>
                            }
                        />

                        <Route
                            path="/settings"
                            element={
                                <PrivateRoute>
                                    <AppLayout withSidebar={false}>
                                        <Settings />
                                    </AppLayout>
                                </PrivateRoute>
                            }
                        >
                            <Route index element={<SettingsIndexRedirect />} />
                            {/* /settings/company saiu para /company (item próprio no menu do utilizador) */}
                            <Route path="company" element={<Navigate to="/company" replace />} />
                            <Route path="general" element={<GeneralSettingsPage />} />
                            <Route path="rentals" element={<RentalsSettingsPage />} />
                            <Route path="parking" element={<ParkingSettingsPage />} />
                            <Route path="finance" element={<FinanceSettingsPage />} />
                            <Route path="purchases" element={<PurchasesSettingsPage />} />
                            <Route path="types" element={<TypesSettingsPage />} />
                            <Route path="roles" element={<RolesSettingsPage />} />
                            <Route path="*" element={<SettingsIndexRedirect />} />
                        </Route>

                        //Gerir o condomínio
                        <Route
                            path="/properties/:id"
                            element={
                                <PrivateRoute>
                                    <AppLayout>
                                        <CondominiumLayout />
                                    </AppLayout>
                                </PrivateRoute>
                            }
                        >
                            <Route index element={<Dashboard />} />
                            <Route path="dashboard/financeiro" element={<Dashboard />} />
                            <Route path="dashboard/acesso" element={<Dashboard />} />
                            <Route path="dashboard/manutencao" element={<Dashboard />} />
                            <Route path="buildings" element={<Buildings />} />
                            <Route path="blocks" element={<Blocks />} />
                            <Route path="units" element={<Units />} />
                            <Route path="residents" element={<Residents />} />
                            <Route path="occupancy-history" element={<OccupancyHistory />} />
                            <Route path="committee" element={<Committee />} />
                            <Route path="finance" element={<Navigate to="overview" replace />} />
                            <Route path="finance/overview" element={<ModuleOverview kind="finance" />} />
                            <Route path="finance/accounts" element={<Accounts />} />
                            <Route path="finance/incomes" element={<Revenues />} />
                            <Route path="finance/charges" element={<Charges />} />
                            <Route path="finance/payments" element={<Payments />} />
                            <Route path="finance/suppliers" element={<Fornecedores />} />
                            <Route path="finance/purchases" element={<Purchases />} />
                            <Route path="finance/expenses" element={<Despesas />} />
                            <Route path="finance/recurring-expenses" element={<RecurringExpenses />} />
                            <Route path="finance/expense-payments" element={<DespesaPayments />} />
                            <Route path="finance/transfers" element={<Transferencias />} />
                            <Route path="access-control" element={<Navigate to="overview" replace />} />
                            <Route path="access-control/overview" element={<ModuleOverview kind="access" />} />
                            <Route path="access-control/visits" element={<AgendamentoVisitas />} />
                            <Route path="access-control/visits-log" element={<Visitas />} />
                            <Route path="access-control/visitors" element={<Visitantes />} />
                            <Route path="access-control/vehicles" element={<Veiculos />} />
                            <Route path="access-control/vehicles-log" element={<RegistosVeiculo />} />
                            <Route path="access-control/packages" element={<Encomendas />} />
                            <Route path="access-control/access-points" element={<AccessPoints />} />
                            <Route path="access-control/shifts" element={<Shifts />} />
                            <Route path="common-areas" element={<EspacosComuns />} />
                            <Route path="common-areas/my-reservations" element={<MinhasReservas />} />
                            <Route path="common-areas/approvals" element={<AprovacaoReservas />} />
                            <Route path="common-areas/calendar" element={<CalendarioReservas />} />
                            <Route path="common-areas/history" element={<HistoricoReservas />} />
                            <Route path="parking" element={<Parking />} />
                            <Route path="parking/slots" element={<ParkingSlots />} />
                            <Route path="parking/assignments" element={<ParkingAssignments />} />
                            <Route path="parking/authorizations" element={<ParkingAuthorizations />} />
                            <Route path="arrendamentos" element={<ArrendamentosIndex />} />
                            <Route path="arrendamentos/meus" element={<MeusArrendamentos />} />
                            <Route path="arrendamentos/cobrancas" element={<CobrancasRenda />} />
                            <Route path="arrendamentos/recebimentos" element={<PagamentosRenda />} />
                            <Route path="arrendamentos/meu-arrendamento" element={<MeuArrendamento />} />
                            <Route path="arrendamentos/faturas" element={<MinhasFaturas />} />
                            <Route path="maintenance" element={<Navigate to="overview" replace />} />
                            <Route path="maintenance/overview" element={<ModuleOverview kind="maintenance" />} />
                            <Route path="maintenance/assets" element={<Assets />} />
                            <Route path="maintenance/assets/:ativoId/history" element={<AssetMaintenanceHistory />} />
                            <Route path="maintenance/requests" element={<MaintenanceRequests />} />
                            <Route path="maintenance/requests/new" element={<MaintenanceRequestForm />} />
                            <Route path="maintenance/requests/:requestId" element={<MaintenanceRequestDetail />} />
                            <Route path="maintenance/service-orders" element={<ServiceOrders />} />
                            <Route path="maintenance/service-orders/new" element={<ServiceOrderForm />} />
                            <Route path="maintenance/service-orders/:orderId" element={<ServiceOrderDetail />} />
                            <Route path="maintenance/service-orders/:orderId/edit" element={<ServiceOrderForm />} />
                            <Route path="maintenance/schedules" element={<MaintenanceSchedules />} />
                            <Route path="maintenance/schedules/new" element={<MaintenanceScheduleForm />} />
                            <Route path="maintenance/schedules/:scheduleId/edit" element={<MaintenanceScheduleForm />} />
                            <Route path="maintenance/sla-config" element={<SlaConfigSettings />} />
                            <Route path="communication" element={<Navigate to="comunicados" replace />} />
                            <Route path="communication/comunicados" element={<Comunicados />} />
                            <Route path="communication/comunicados/new" element={<ComunicadoForm />} />
                            <Route path="communication/comunicados/:comunicadoId" element={<ComunicadoDetail />} />
                            <Route path="communication/comunicados/:comunicadoId/edit" element={<ComunicadoForm />} />
                            <Route path="communication/templates" element={<Templates />} />
                            <Route path="documents" element={<Documentos />} />
                            <Route path="assembleias" element={<Assembleias />} />
                            <Route path="assembleias/new" element={<AssembleiaForm />} />
                            <Route path="assembleias/votacoes" element={<Votacoes />} />
                            <Route path="assembleias/votacoes/new" element={<VotacaoForm />} />
                            <Route path="assembleias/votacoes/:votacaoId" element={<VotacaoDetail />} />
                            <Route path="assembleias/votacoes/:votacaoId/edit" element={<VotacaoForm />} />
                            <Route path="assembleias/:assembleiaId" element={<AssembleiaDetail />} />
                            <Route path="assembleias/:assembleiaId/edit" element={<AssembleiaForm />} />
                            <Route path="assembleias/:assembleiaId/votacoes/new" element={<VotacaoForm />} />
                            <Route path="assembleias/:assembleiaId/votacoes/:votacaoId" element={<VotacaoDetail />} />
                            <Route path="assembleias/:assembleiaId/votacoes/:votacaoId/edit" element={<VotacaoForm />} />
                            <Route path="users" element={<Users />} />
                        </Route>

                    </Route>
                </Routes>
              </Suspense>
            </BrowserRouter>
        </AppThemeProvider>

    )
}
